import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import api from '../lib/api';
import { PersonaAiBadge } from '../components/PersonaAiBadge';
import { isAiGeneratedPersona } from '../lib/personaSource';
import { formatScheduleDate } from '../lib/formatScheduleDate';
import { isOrgManager } from '../lib/orgRoles';
import { RecordingPrivateBadge } from '../components/practice/RecordingPrivateNotice';
import type { RecordingAccess } from '../components/practice/types';

interface UsageInfo {
  inputTokens: number;
  outputTokens: number;
  callCount: number;
}

interface Session {
  id: string;
  userName?: string | null;
  userEmail?: string;
  personaName: string;
  personaSource?: string;
  personaCount?: number;
  personaNames?: string[];
  goalLabel: string | null;
  pitchStyle: string;
  status: string;
  messageCount: number;
  goalScore: number | null;
  goalReached: boolean | null;
  createdAt: string;
  usage?: UsageInfo;
  access?: RecordingAccess;
}

type Tab = 'mine' | 'team';

interface UpcomingSchedule {
  id: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  title: string | null;
  status: string;
  host: { name: string | null; email: string };
  visibility: string;
}

function formatTokenTotal(input: number, output: number): string {
  const t = input + output;
  if (t >= 1_000_000) return `${(t / 1_000_000).toFixed(2)}M tokens`;
  if (t >= 1_000) return `${(t / 1_000).toFixed(1)}k tokens`;
  return `${t} tokens`;
}

export function PracticeListPage() {
  const { user } = useAuthContext();
  const canViewTeam = isOrgManager(user?.orgRole);
  const [tab, setTab] = useState<Tab>('mine');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [totals, setTotals] = useState<UsageInfo | null>(null);
  const [upcomingSchedules, setUpcomingSchedules] = useState<UpcomingSchedule[]>([]);
  const [practiceUsageReadonly, setPracticeUsageReadonly] = useState(false);

  useEffect(() => {
    api
      .get('/orgs/me')
      .then(async (r) => {
        setPracticeUsageReadonly(Boolean(r.data?.usage?.practiceSessions?.readonly));
        const features: string[] = r.data?.features ?? [];
        if (!features.includes('practice_scheduling')) return;
        const s = await api.get('/practice/schedules');
        if (s.data?.schedules) setUpcomingSchedules(s.data.schedules);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const url = tab === 'team' ? '/practice/sessions/org' : '/practice/sessions';
    setSessions([]);
    setTotals(null);
    api
      .get(url)
      .then((res) => {
        const body = res.data;
        if (body?.sessions && body?.totals) {
          setSessions(body.sessions);
          setTotals(body.totals);
        } else {
          setSessions(Array.isArray(body) ? body : []);
        }
      })
      .catch(() => {});
  }, [tab]);

  const isListEmpty = sessions.length === 0;
  const newSessionButtonClass =
    'inline-flex items-center justify-center bg-green-700 text-white px-6 py-3 rounded-md text-base font-semibold hover:bg-green-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2';

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Practice Sessions</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            {tab === 'team' ? 'All team practice sessions.' : 'Your sales practice history.'}
          </p>
        </div>
        {!isListEmpty &&
          (practiceUsageReadonly ? (
            <span
              className="bg-gray-200 text-gray-500 px-4 py-2 rounded-md font-medium text-center sm:text-left shrink-0 cursor-not-allowed"
              title="Monthly practice session limit reached"
            >
              New Session
            </span>
          ) : (
            <Link
              to="/practice/new"
              className="bg-green-700 text-white px-4 py-2 rounded-md font-medium hover:bg-green-800 transition-colors text-center sm:text-left shrink-0"
            >
              New Session
            </Link>
          ))}
      </div>

      {upcomingSchedules.length > 0 && tab === 'mine' && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50/80 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Upcoming scheduled practice</h2>
          <ul className="space-y-2">
            {upcomingSchedules.map((sch) => (
              <li key={sch.id}>
                <Link
                  to={`/practice/schedule/${sch.id}`}
                  className="text-green-800 hover:underline font-medium"
                >
                  {sch.title || 'Practice session'}
                </Link>
                <span className="text-gray-600 text-sm ml-2">
                  {formatScheduleDate(sch.scheduledStartAt)} · {sch.host.name || sch.host.email}
                  {sch.visibility === 'org_open' ? ' · Org' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canViewTeam && (
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setTab('mine')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === 'mine'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            My Sessions
          </button>
          <button
            onClick={() => setTab('team')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === 'team'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Team Sessions
          </button>
        </div>
      )}

      {totals && sessions.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border text-sm text-gray-700">
          <span className="font-medium">Total ({sessions.length} sessions): </span>
          {formatTokenTotal(totals.inputTokens, totals.outputTokens)}
          {totals.callCount > 0 && (
            <span className="text-gray-500"> &middot; {totals.callCount} AI calls</span>
          )}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="bg-white p-12 rounded-lg border text-center">
          <p className="text-gray-500 mb-6">
            {tab === 'team'
              ? 'No team practice sessions yet.'
              : 'No practice sessions yet. Start your first at-bat!'}
          </p>
          {practiceUsageReadonly ? (
            <span
              className="inline-flex items-center justify-center bg-gray-200 text-gray-500 px-6 py-3 rounded-md text-base font-semibold cursor-not-allowed"
              title="Monthly practice session limit reached"
            >
              Start practicing
            </span>
          ) : (
            <Link
              to="/practice/new"
              data-testid="practice-list-start-practicing"
              className={newSessionButtonClass}
            >
              Start practicing
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border divide-y">
          {sessions.map((session) => (
            <Link
              key={session.id}
              to={`/practice/${session.id}`}
              className="block p-3 sm:p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                    <span className="truncate">
                      {(session.personaCount ?? 1) > 1
                        ? session.personaNames?.join(', ') || session.personaName
                        : session.personaName}
                    </span>
                    {(session.personaCount ?? 1) > 1 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Committee
                      </span>
                    )}
                    {isAiGeneratedPersona(session.personaSource) && <PersonaAiBadge />}
                    {session.access === 'summary' && <RecordingPrivateBadge />}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {tab === 'team' && session.userEmail && (
                      <span className="text-gray-700 font-medium">
                        {session.userName || session.userEmail}
                        {' \u00B7 '}
                      </span>
                    )}
                    {session.pitchStyle} &middot; {session.messageCount} messages
                    {session.goalLabel && (
                      <span className="ml-1">&middot; {session.goalLabel}</span>
                    )}
                    {session.usage &&
                      (session.usage.inputTokens > 0 || session.usage.outputTokens > 0) && (
                        <span className="ml-1">
                          &middot;{' '}
                          {formatTokenTotal(session.usage.inputTokens, session.usage.outputTokens)}
                        </span>
                      )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {session.goalScore !== null && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        session.goalReached
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {session.goalScore}/10
                    </span>
                  )}
                  <span className="text-xs sm:text-sm text-gray-400">
                    {new Date(session.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
